import type { ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

const NAV = [
  { to: '/about', label: 'About' },
  { to: '/academy', label: 'Professional Learning' },
  { to: '/laboratories', label: 'Virtual Laboratories' },
  { to: '/institutions', label: 'Institutional Solutions' },
  { to: '/membership', label: 'Membership' },
  { to: '/contact', label: 'Contact' },
];

/**
 * The public site's chrome.
 *
 * These ~8 routes are prerendered to static HTML at build time
 * (`scripts/prerender.mjs`), which is the answer to the plan's first known
 * risk: a Vite SPA renders nothing to a crawler, and the outline has a
 * substantial public surface that has to be indexable. Prerendering keeps one
 * codebase; SSR was rejected because the authenticated app — the other 95% —
 * gains nothing from it.
 *
 * The practical constraint that follows: nothing under `public/` may read the
 * session, call the API on mount, or touch `window` during render. It has to
 * produce the same HTML in Node as in a browser.
 */
export function PublicLayout({ children }: { children?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-white">
      <header className="border-b border-ink-200">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <span aria-hidden="true" className="text-2xl">
              🔬
            </span>
            <span className="text-sm font-bold leading-tight text-brand-900">
              STEM Africa
              <span className="block text-[0.7rem] font-medium text-ink-500">Learning Hub</span>
            </span>
          </Link>

          <nav className="ml-auto flex flex-wrap items-center gap-1 text-sm">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 font-medium ${
                    isActive ? 'text-brand-800' : 'text-ink-600 hover:text-ink-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/login" className="btn-primary ml-2">
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      {/*
        `children` when the prerenderer calls this directly, `<Outlet />` when
        the router does. One layout component either way — a second copy for the
        static build is a second copy to forget to update.
      */}
      <main className="flex-1">{children ?? <Outlet />}</main>

      <footer className="border-t border-ink-200 bg-ink-50">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-semibold text-ink-900">STEM Africa Learning Hub</p>
            <p className="mt-1 text-ink-600">
              A subsidiary of VISINA Consult Limited — professional learning, teaching resources and
              virtual laboratories for educators across Africa.
            </p>
          </div>
          <div>
            <p className="font-semibold text-ink-900">Explore</p>
            <ul className="mt-1 space-y-1 text-ink-600">
              <li>
                <Link to="/academy" className="hover:underline">
                  Professional Learning Academy
                </Link>
              </li>
              <li>
                <Link to="/laboratories" className="hover:underline">
                  Virtual STEM Laboratory
                </Link>
              </li>
              <li>
                <Link to="/membership" className="hover:underline">
                  Membership &amp; subscription
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ink-900">Institutions</p>
            <ul className="mt-1 space-y-1 text-ink-600">
              <li>
                <Link to="/institutions" className="hover:underline">
                  Schools &amp; TTCs
                </Link>
              </li>
              <li>
                <Link to="/institutions" className="hover:underline">
                  Ministries of Education
                </Link>
              </li>
              <li>
                <Link to="/institutions" className="hover:underline">
                  NGOs &amp; development partners
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ink-900">Verify a certificate</p>
            <p className="mt-1 text-ink-600">
              Anyone can check a certificate serial without an account.
            </p>
            <Link to="/verify" className="mt-2 inline-block text-brand-700 underline">
              Open the verification page
            </Link>
          </div>
        </div>
        <div className="border-t border-ink-200 py-4 text-center text-xs text-ink-500">
          © {new Date().getFullYear()} VISINA Consult Limited. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
